import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, ArrowUpRight, Loader2, CheckCircle2, Instagram, Linkedin } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import { Reveal, SectionLabel, Stagger, Item } from "../components/Reveal";
import { WhatsAppButton } from "../components/CTA";
import { useContent, waLink, telLink, mailLink } from "../data/siteContent";

const envUrl = process.env.REACT_APP_BACKEND_URL;
const API = `${envUrl && new URL(envUrl).host === window.location.host ? envUrl : window.location.origin}/api`;

const needOptions = ["Website", "Logo & Brand", "WhatsApp & Automation", "SOPs & Operations", "Quikpik for my cafe", "Other"];

const inputCls =
  "w-full border border-line bg-base px-4 py-3.5 text-sm text-txt placeholder-faint outline-none transition-colors duration-300 focus:border-myrtle";

const emptyForm = { name: "", phone: "", email: "", business_type: "", need: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [done, setDone] = useState(false);
  const content = useContent();
  const { contact } = content;

  const details = [
    { icon: Phone, label: "WhatsApp (fastest)", value: contact.phone_display, href: waLink(content), testid: "contact-whatsapp-link" },
    { icon: Phone, label: "Call", value: contact.phone_display, href: telLink(content), testid: "contact-phone-link" },
    { icon: Mail, label: "Email", value: contact.email, href: mailLink(content), testid: "contact-email-link" },
    { icon: Clock, label: "Hours", value: contact.hours, href: null, testid: "contact-hours" },
    { icon: MapPin, label: "Office", value: contact.address, href: contact.maps_url || null, testid: "contact-address" },
  ];
  const socials = [
    { icon: Instagram, href: contact.instagram, testid: "contact-instagram-link", label: "Instagram" },
    { icon: Linkedin, href: contact.linkedin, testid: "contact-linkedin-link", label: "LinkedIn" },
  ].filter((s) => s.href);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const normalizedPhone = (v) => {
    let digits = v.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
    return digits;
  };

  const onPhoneChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, phone: value });
    const digits = normalizedPhone(value);
    setPhoneError(value.trim() && digits.length !== 10 ? "Enter a valid 10-digit phone number" : "");
  };

  const submit = async (e) => {
    e.preventDefault();
    const digits = normalizedPhone(form.phone);
    if (digits.length !== 10) {
      setPhoneError("Enter a valid 10-digit phone number");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: digits });
      setDone(true);
      setForm(emptyForm);
    } catch (err) {
      toast.error("Couldn't send your message. Please WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact Allcov — Free 5-Minute Audit" description="Get a free 5-minute audit of your Google listing, website and WhatsApp. We reply within one working day." path="/contact" />
      <PageHero
        eyebrow="Contact"
        title="Tell us what's slowing you down."
        line="Get a free 5-minute audit of your Google listing, website and WhatsApp. We reply within one working day."
        testid="contact"
      />

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
        <div className="grid gap-16 lg:grid-cols-12">
          {/* DETAILS */}
          <div className="lg:col-span-5">
            <Reveal><SectionLabel>Reach Us Directly</SectionLabel></Reveal>
            <Stagger className="mt-4 space-y-7">
              {details.map((d) => (
                <Item key={d.label}>
                  <div className="flex items-start gap-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-line bg-surface">
                      <d.icon size={18} className="text-pop" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] text-faint">{d.label}</p>
                      {d.href ? (
                        <a href={d.href} data-testid={d.testid} className="mt-1.5 block text-base text-txt transition-colors duration-300 hover:text-pop md:text-lg">
                          {d.value}
                        </a>
                      ) : (
                        <p data-testid={d.testid} className="mt-1.5 max-w-sm text-base text-txt md:text-lg">{d.value}</p>
                      )}
                    </div>
                  </div>
                </Item>
              ))}
            </Stagger>
            {socials.length > 0 && (
              <div className="mt-10 flex items-center gap-4" data-testid="contact-socials">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="flex h-11 w-11 items-center justify-center border border-line text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop"
                  >
                    <s.icon size={17} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* FORM / SUCCESS */}
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              {done ? (
                <div className="border border-line bg-surface p-10 text-center md:p-14" data-testid="contact-success">
                  <CheckCircle2 size={40} className="mx-auto text-pop" strokeWidth={1.5} />
                  <p className="mt-6 font-batangas text-3xl tracking-tight">Thanks!</p>
                  <p className="mt-3 text-sm text-mut md:text-base">We will WhatsApp you within one working day. Can't wait?</p>
                  <div className="mt-8 flex justify-center">
                    <WhatsAppButton testid="contact-success-whatsapp" />
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="border border-line bg-surface p-8 md:p-10">
                  <p className="text-xs uppercase tracking-[0.25em] text-faint">Send a Message</p>
                  <div className="mt-7 space-y-6">
                    <div>
                      <label htmlFor="name" className="text-sm text-mut">Name *</label>
                      <input id="name" name="name" required value={form.name} onChange={update} placeholder="Your full name" data-testid="contact-name-input" className={`${inputCls} mt-2`} />
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="phone" className="text-sm text-mut">Phone *</label>
                        <input
                          id="phone" name="phone" type="tel" inputMode="tel" required value={form.phone} onChange={onPhoneChange}
                          placeholder="+91 98765 43210" data-testid="contact-phone-input"
                          className={`${inputCls} mt-2`}
                          style={phoneError ? { borderColor: "#f87171" } : undefined}
                        />
                        {phoneError && (
                          <p data-testid="contact-phone-error" className="mt-2 text-xs text-red-400">{phoneError}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="email" className="text-sm text-mut">Email (optional)</label>
                        <input id="email" name="email" type="email" value={form.email} onChange={update} placeholder="you@company.com" data-testid="contact-email-input" className={`${inputCls} mt-2`} />
                      </div>
                    </div>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="business_type" className="text-sm text-mut">Business type</label>
                        <input id="business_type" name="business_type" value={form.business_type} onChange={update} placeholder="Clinic, cafe, coaching…" data-testid="contact-business-input" className={`${inputCls} mt-2`} />
                      </div>
                      <div>
                        <label htmlFor="need" className="text-sm text-mut">What do you need?</label>
                        <select id="need" name="need" value={form.need} onChange={update} data-testid="contact-need-select" className={`${inputCls} mt-2 ${form.need ? "" : "text-faint"}`}>
                          <option value="" disabled>Select one</option>
                          {needOptions.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="message" className="text-sm text-mut">Message (optional)</label>
                      <textarea id="message" name="message" rows={4} value={form.message} onChange={update} placeholder="Anything we should know?" data-testid="contact-message-input" className={`${inputCls} mt-2 resize-none`} />
                    </div>
                    <button
                      type="submit" disabled={sending} data-testid="contact-submit-button"
                      className="group inline-flex w-full items-center justify-center gap-2 bg-myrtle px-8 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-myrtle-light disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {sending ? (
                        <>Sending <Loader2 size={16} className="animate-spin" /></>
                      ) : (
                        <>
                          Send Message
                          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
